import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { line, Color, EXEMPT_CUSTOMER_STEPS, Size, exemptCustomerPath, exemptCustomerSteps, t, type ExemptCustomerStep, type ExemptCustomerView, type StringKey } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { SetupStep, SetupSteps, type SetupStepState } from '../../components/composites/SetupSteps';
import { TAX_FORMS_ROUTE, taxFormPath } from '../accounting/api';
import { useApi } from './api';
import styles from './RecordPage.module.css';

/**
 * Exempt Customer Steps (piece 25; decisions 1781, 2851): four steps in a
 * window the Site page's Tax exempt certificates tile opens from its header,
 * drawn through Setup Steps (decision 1647). 🔒 Each step's state is read from
 * the site's certificate requests and certificates through the tax controller,
 * and never stored.
 *
 * 🔨 The steps carry no control of their own beyond the site's: Record a
 * certificate and Edit open the site's one certificate window, and the Request
 * a certificate panel stands under the four steps, so nothing is drawn twice.
 */
const TITLE: Readonly<Record<ExemptCustomerStep, StringKey>> = {
  findForm: 'site.exemptSteps.findForm.title',
  requestOrRecord: 'site.exemptSteps.requestOrRecord.title',
  checkFields: 'site.exemptSteps.checkFields.title',
  confirm: 'site.exemptSteps.confirm.title',
};

const BODY: Readonly<Record<ExemptCustomerStep, StringKey>> = {
  findForm: 'site.exemptSteps.findForm.body',
  requestOrRecord: 'site.exemptSteps.requestOrRecord.body',
  checkFields: 'site.exemptSteps.checkFields.body',
  confirm: 'site.exemptSteps.confirm.body',
};

/** 🔒 What a later step waits for, drawn on its card (decision 1970): a state on the site's address, then a certificate. */
const WAITS: Readonly<Record<ExemptCustomerStep, StringKey>> = {
  findForm: 'site.exemptSteps.waits.state',
  requestOrRecord: 'site.exemptSteps.waits.state',
  checkFields: 'site.exemptSteps.waits.certificate',
  confirm: 'site.exemptSteps.waits.certificate',
};

/** What each step found, in a sentence; a done step's summary is the same line. */
function found(view: ExemptCustomerView, step: ExemptCustomerStep): string {
  const certificate = view.certificate;
  if (!view.stateCode) return t('site.exemptSteps.noState');
  switch (step) {
    case 'findForm':
      return view.form ? line(t('site.exemptSteps.findForm.form'), view.stateName, t('site.exemptSteps.findForm.is'), view.form.form) : line(t('site.exemptSteps.findForm.none'), view.stateName);
    case 'requestOrRecord':
      if (certificate) return line(t('site.exemptSteps.requestOrRecord.recorded'), certificate.effectiveFrom);
      if (view.openRequests > 0) return line(t('site.exemptSteps.requestOrRecord.open'), String(view.openRequests));
      return t('site.exemptSteps.requestOrRecord.none');
    case 'checkFields': {
      if (!certificate) return t('site.exemptSteps.noCertificate');
      const missing = [
        certificate.formType?.trim() ? null : t('site.exemptSteps.checkFields.formType'),
        certificate.exemptionReason?.trim() ? null : t('site.exemptSteps.checkFields.reason'),
      ].filter((one): one is string => one !== null);
      return missing.length === 0
        ? line(t('site.exemptSteps.checkFields.read'), certificate.formType, '·', certificate.exemptionReason, '·', t('site.exemptSteps.checkFields.from'), certificate.effectiveFrom)
        : line(t('site.exemptSteps.checkFields.missing'), missing.join(', '));
    }
    case 'confirm':
      if (!certificate) return t('site.exemptSteps.noCertificate');
      return certificate.reviewedAt ? t('site.exemptSteps.confirm.reviewed') : t('site.exemptSteps.confirm.awaiting');
    default:
      return '';
  }
}

export interface ExemptCustomerStepsViewProps {
  view: ExemptCustomerView;
  apiBaseUrl?: string;
  /** Opens the site's own certificate window empty, the one Record a certificate opens; absent for a reader. */
  onRecordCertificate?: () => void;
  /** Opens the site's certificate window on the certificate the steps read; absent for a reader. */
  onEditCertificate?: (certificateId: string) => void;
  /** 🔒 Decision 7891: the certificate card's Move and Remove for the certificate the steps read, where the card offers them; absent for a reader. */
  certificateActs?: (certificateId: string) => readonly { key: string; label: string; onAct: () => void }[];
  /** 🔒 Decision 2851: the Request a certificate panel, drawn under the four steps and never inside one, so a step that reads Done and folds takes nothing with it. */
  requestPanel?: ReactNode;
}

/** The four steps over one answer, so a test and the Component Test Page draw each state with no route behind it. */
export function ExemptCustomerStepsView({ view, apiBaseUrl = '', onRecordCertificate, onEditCertificate, certificateActs, requestPanel }: ExemptCustomerStepsViewProps) {
  const [opened, setOpened] = useState<ReadonlySet<ExemptCustomerStep>>(new Set());
  const toggle = (step: ExemptCustomerStep) => setOpened((was) => {
    const next = new Set(was);
    if (next.has(step)) next.delete(step);
    else next.add(step);
    return next;
  });
  const states = exemptCustomerSteps(view);
  const stateOf = (step: ExemptCustomerStep): SetupStepState => (states[step].done ? 'done' : states[step].reachable ? 'current' : 'later');
  const certificate = view.certificate;
  const control = (step: ExemptCustomerStep): ReactNode => {
    switch (step) {
      case 'findForm':
        return (
          <>
            {(view.form?.files ?? []).map((file) => <a key={file.file} href={`${apiBaseUrl}${taxFormPath(file.file)}`} target="_blank" rel="noopener noreferrer" data-exempt-step-form={file.file}>{file.title}</a>)}
            {view.form && view.form.files.length === 0 && view.form.url ? <a href={view.form.url} target="_blank" rel="noopener noreferrer">{view.form.form}</a> : null}
            <Link to={TAX_FORMS_ROUTE} data-exempt-step-library="true">{t('site.taxJurisdiction.library')}</Link>
          </>
        );
      case 'requestOrRecord':
        return onRecordCertificate ? <Button size={Size.Small} color={Color.Navy} text={[t('site.taxJurisdiction.recordCertificate')]} actions={[onRecordCertificate]} /> : null;
      case 'checkFields':
      case 'confirm':
        if (!certificate) return null;
        return (
          <>
            {onEditCertificate ? <Button size={Size.Small} text={[t('site.exemptSteps.edit')]} actions={[() => onEditCertificate(certificate.id)]} /> : null}
            {(certificateActs?.(certificate.id) ?? []).map((act) => <Button key={act.key} size={Size.Small} text={[act.label]} actions={[act.onAct]} />)}
          </>
        );
      default:
        return null;
    }
  };
  return (
    <>
    <SetupSteps dataAttribute={{ 'data-exempt-steps': view.siteId }}>
      {EXEMPT_CUSTOMER_STEPS.map((step, index) => {
        const act = stateOf(step) === 'later' ? null : control(step);
        return (
          <SetupStep
            key={step}
            hook="exempt"
            number={index + 1}
            name={step}
            title={t(TITLE[step])}
            state={stateOf(step)}
            open={opened.has(step)}
            onToggle={() => toggle(step)}
            summary={<p className={styles.hint}>{found(view, step)}</p>}
            holdReason={stateOf(step) === 'later' ? t(WAITS[step]) : null}
          >
            <p className={styles.hint} data-exempt-step-found={step}>{found(view, step)}</p>
            <p className={styles.hint}>{t(BODY[step])}</p>
            {act ? <div className={styles.actions}>{act}</div> : null}
          </SetupStep>
        );
      })}
    </SetupSteps>
    {stateOf('requestOrRecord') !== 'later' ? requestPanel : null}
    </>
  );
}

/**
 * 🔒 The Exempt Customer Steps over a read of their own (decisions 1781, 2851). The read is the Accounting read's,
 * as the tile's own is (decision 1633), so a reader the route refuses sees nothing here. It reads again whenever the
 * page's own answer is read again, so a certificate saved or a request sent redraws the steps.
 */
export function ExemptCustomerSteps({ siteId, apiBaseUrl = '', revision, onRecordCertificate, onEditCertificate, certificateActs, requestPanel }: Omit<ExemptCustomerStepsViewProps, 'view'> & { siteId: string; revision: unknown }) {
  const fetched = useApi<ExemptCustomerView>(exemptCustomerPath(siteId), apiBaseUrl);
  const first = useRef(true);
  const { reload } = fetched;
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    reload();
  }, [revision, reload]);
  if (fetched.denied) return null;
  const view = fetched.state.kind === 'ready' ? fetched.state.data : null;
  return (
    <div className={styles.verify} data-exempt-customer={siteId}>
      {/* 🔨 An answer missing a part draws as loading rather than throwing (CLAUDE.md). */}
      {view && 'openRequests' in view ? (
        <ExemptCustomerStepsView view={view} apiBaseUrl={apiBaseUrl} {...(onRecordCertificate ? { onRecordCertificate } : {})} {...(onEditCertificate ? { onEditCertificate } : {})} {...(certificateActs ? { certificateActs } : {})} {...(requestPanel ? { requestPanel } : {})} />
      ) : fetched.state.kind === 'failed' ? <p className={styles.hint}>{fetched.state.error}</p> : <p className={styles.hint}>{t('async.loading')}</p>}
    </div>
  );
}

/**
 * 🔒 The Exempt Customer Steps window (decision 2851), opened from the Site
 * page's certificates tile. It is unmounted when closed, so each opening reads
 * the steps afresh, and it stays open beneath the certificate window that Record
 * a certificate and Edit open on top of it.
 */
export function ExemptCustomerStepsWindow({ open, onCancel, ...steps }: Omit<ExemptCustomerStepsViewProps, 'view'> & { siteId: string; revision: unknown; open: boolean; onCancel: () => void }) {
  return (
    <Modal open={open} title={t('site.exemptSteps.title')} onCancel={onCancel} width="wide" dataModal="exempt-customer-steps" actions={[{ kind: 'close', label: t('modal.close'), onAct: onCancel }]}>
      {open ? <ExemptCustomerSteps {...steps} /> : null}
    </Modal>
  );
}
